import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import ArrowUpRightIcon from "@redotech/redo-icons/arrow-up-right_filled.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_ONBOARDING_APP_URL } from "@redotech/redo-merchant-app-common/config";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { InstallSource } from "@redotech/redo-model/shopify";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentOnboarding, onboardingTc } from "../client/onboarding";
import { useReclaimAsyncOnboardingEnabled } from "../setup-guide/provider/feature-flag";
import * as indexCss from "./index.module.css";
import { shouldRedirect } from "./should-redirect";

const tcUrl = new URL("https://www.getredo.com/terms-conditions");
const TC_IMAGE_URL =
  "https://data.getredo.com/redo/static/onboarding/t-c-accept.webp";

/**
 * Builds an onboarding-app URL with the merchant auth handoff embedded as
 * query params. Used by the user-profile menu's "Onboarding" entry and the
 * /onboarding-redirect login bounce. The post-T&C accept flow uses a
 * server-resolved URL (see PUT /onboarding/tc).
 */
export function autoOnboardingUrl(token: string, teamId: string) {
  const url = new URL(REDO_ONBOARDING_APP_URL);
  url.searchParams.set("token", token);
  url.searchParams.set("teamId", teamId);
  return url;
}

export const Onboarding = memo(function Onboarding() {
  const team = useContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const navigate = useNavigate();
  const reclaimAsyncOnboardingEnabled = useReclaimAsyncOnboardingEnabled();
  const [showModal, setShowModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);

  const isShopifyTestStore =
    team?.name.toLowerCase().includes("shpfy test") ||
    team?.email.endsWith("@shopify.com") ||
    false;

  const [acceptTermsLoad, doAcceptTerms] = useTriggerLoad(async (signal) => {
    return alertOnFailure("Failed to onboard")(async () => {
      const response = await onboardingTc(client, { signal });

      // Flag-on Reclaim merchants stay inside the merchant app — the
      // setup-guide provider's entry-source hook will create the
      // progress row from `?setupSource=tc_accept` and pop the intake
      // modal. Bypasses the legacy setup.getredo.com redirect entirely.
      // installSource isn't on the typed Team interface (mirrors the
      // server-side cast in onboarding.ts).
      const installSource = (team as { installSource?: InstallSource } | null)
        ?.installSource;
      const teamId = team?._id?.toString();
      if (
        reclaimAsyncOnboardingEnabled &&
        installSource === InstallSource.RECLAIM_APP &&
        teamId
      ) {
        navigate(
          `/stores/${teamId}/finance/disputes/dashboard?setupSource=tc_accept`,
        );
        setShowModal(false);
        return true;
      }

      const redirectTo = shouldRedirect({ response, isShopifyTestStore });
      if (redirectTo) {
        // Keep the "Setting up..." pending state visible through the
        // cross-domain navigation so the user doesn't see a blank flash.
        // The promise resolves with `true` only as a watchdog fallback if
        // the navigation never occurs (popup-blocked sandboxed iframe, CSP
        // refusal, extension-blocked redirect) — we hide the modal so the
        // user isn't stranded on a permanent "Setting up..." state.
        location.href = redirectTo;
        return new Promise<true>((resolve) => {
          setTimeout(() => {
            setShowModal(false);
            resolve(true);
          }, 5000);
        });
      }
      // Fallback: stay on the merchant app. Either we're a Shopify test
      // store, or the server returned an unexpected redirectUrl.
      setShowModal(false);
      return true;
    });
  });

  const onboardingLoad = useLoad(async (signal) => {
    const data = await getCurrentOnboarding(client, { signal });
    if (data.tcConfirmed) {
      setShowModal(false);
      return data;
    }
    // Retry once after a short delay to guard against read replica lag
    // returning a stale tcConfirmed=false for merchants who already accepted
    await new Promise((r) => setTimeout(r, 1500));
    if (signal.aborted) {
      return data;
    }
    const retry = await getCurrentOnboarding(client, { signal });
    setShowModal(!retry.tcConfirmed);
    return retry;
  }, []);

  if (!showModal) {
    return null;
  }

  if (acceptTermsLoad.pending) {
    return (
      <RedoModal
        hideCloseButton
        isOpen
        modalSize="sm"
        muiClassName={indexCss.backdropBlur}
        onModalCloseRequested={() => {}}
        primaryButton={{
          text: "Setting up...",
          onClick: () => {},
          pending: true,
        }}
        title="Setting up Redo"
      />
    );
  }

  if (acceptTermsLoad.error || onboardingLoad.error) {
    return (
      <RedoModal
        hideCloseButton
        isOpen
        modalSize="sm"
        muiClassName={indexCss.backdropBlur}
        onModalCloseRequested={() => {}}
        title="Welcome to Redo"
      >
        <Text fontSize="sm" textColor="tertiary">
          There was an error during onboarding.
        </Text>
      </RedoModal>
    );
  }

  return (
    <RedoModal
      hideCloseButton
      hideFooter
      hideHeader
      isOpen
      modalSize="md"
      muiClassName={indexCss.backdropBlur}
      noPadding
      onModalCloseRequested={() => {}}
      title=""
    >
      <Flex className={indexCss.splitLayout} dir="row">
        {imageLoaded && (
          <div className={indexCss.sideImageWrapper}>
            <img
              alt="Welcome to Redo"
              className={indexCss.sideImage}
              onError={() => setImageLoaded(false)}
              src={TC_IMAGE_URL}
            />
          </div>
        )}
        <Flex
          className={imageLoaded ? indexCss.sideContent : indexCss.fullContent}
          dir="column"
          justify="space-between"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xl" fontWeight="semibold" textColor="primary">
              Welcome to Redo
            </Text>
            <Text fontSize="sm" textColor="tertiary">
              Please review and accept the terms and conditions to continue.
            </Text>
          </Flex>
          <Flex align="flex-start" dir="column" gap="md">
            <a
              className={indexCss.link}
              href={tcUrl.toString()}
              rel="noreferrer"
              target="_blank"
            >
              <Text fontSize="sm" textColor="primary">
                Read terms and conditions
              </Text>
              <ArrowUpRightIcon className={indexCss.linkIcon} />
            </a>
            <RedoButton
              className={indexCss.acceptButton}
              hierarchy="primary"
              onClick={() => doAcceptTerms()}
              size="md"
              text="Accept"
              theme="normal"
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
